<script setup lang="ts">
import { ref, computed, onMounted } from "vue";
import { Header, AppData } from "ram-components";
import SecureLS from "secure-ls";
import { useVisitorData } from "@fingerprintjs/fingerprintjs-pro-vue-v3";
import jwtDecode from "jwt-decode";

import Greeting from "../components/Greeting.vue";
import Apps from "../components/Apps.vue";
import Footer from "../components/Footer.vue";

import Services from "../services";
import { useAuthStore } from "../stores/authStore";
import moreAppsSvg from "../assets/images/moreApps.svg";

const authStore = useAuthStore();

const ls = new SecureLS({
  encodingType: "rc4",
  isCompression: false,
  encryptionSecret: import.meta.env.RAM_STORAGE_SECRET,
});

const recentApps = ref([] as Array<AppData>);
const favoriteApps = ref([] as Array<AppData>);

const inactiveTimeout = computed(() => {
  const mins = jwtDecode<{ eat: number }>(authStore.authState.idToken).eat;
  return `${mins} minute${mins > 1 ? "s" : ""}`;
});

const { data, error, isLoading, getData } = useVisitorData({
  linkedId: authStore.authState.user.email,
});

onMounted(() => {
  if (authStore.isAuthenticated) {
    getData();
    heartBeatService();
  }

  if (!authStore.mfaState.isMFACompleted) {
    authStore.refreshLogin();
  }
});

async function heartBeatService() {
  const controller = new AbortController();
  const timer = setTimeout(() => controller.abort(), 1000);

  try {
    const { data: status } = await Services.getSystemStatus();
    const { data: serviceRes } = await Services.sentDataService(status);

    if (serviceRes) {
      await Services.triggerHeartbeat();
    }
  } catch (err) {
    const message = err instanceof Error ? err.message : 'Unknown error';
    console.warn(`Fingerprint skipped: ${message}`);
  } finally {
    clearTimeout(timer);
  }
}

function handleInit(data: any) {
  // Code for Backwards compatibility, remove once we no longer use Vue2 Apps
  const { businessAreas } = data;
  const user = JSON.parse(ls.get("user"));
  user.IsInternalUser = businessAreas.some((businessArea: any) =>
    businessArea.CompanyName.startsWith("RAM "),
  );
  user.BusinessAreas = businessAreas;
  ls.set("user", JSON.stringify(user));
}

function handleAppsUpdate(data: any) {
  recentApps.value = data.recent;
  favoriteApps.value = data.favorites;
}
</script>

<template>
  <div class="flex h-full w-full flex-col" v-if="authStore.isAuthenticated">
    <Header selfAuth @init="handleInit" @apps="handleAppsUpdate" />
    <div class="flex grow flex-col space-y-5 overflow-y-auto p-8">
      <Greeting />
      <Apps :recent="recentApps" :favorites="favoriteApps" />
      <div class="grow" v-if="recentApps.length || favoriteApps.length"></div>
      <div class="flex justify-center">
        <div
          class="bg-ram-grey/10 p-2 border border-ram-grey rounded-md text-ram-grey text-sm text-center"
        >
          <p>
            <b
              >You'll Be Logged Out After {{ inactiveTimeout }} of Inactivity</b
            >
          </p>
          <br />
          <p>
            For your security, App.RAM will automatically log you out if there
            is no activity across all applications for more than
            {{ inactiveTimeout }}.
          </p>
          <br />
          <p>
            Simply click the Login button to return to the App.RAM home page and
            <b>log in</b> again.
          </p>
        </div>
      </div>
      <div
        class="flex items-center justify-center"
        v-if="recentApps.length || favoriteApps.length"
      >
        <img :src="moreAppsSvg" alt="More Apps" class="h-32" />
      </div>
      <Footer />
    </div>
  </div>
</template>
